import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import PhoneRounded from '@mui/icons-material/PhoneRounded';
import PhoneInTalkRounded from '@mui/icons-material/PhoneInTalkRounded';
import AccessTimeRounded from '@mui/icons-material/AccessTimeRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import SpeedRounded from '@mui/icons-material/SpeedRounded';
import BoltRounded from '@mui/icons-material/BoltRounded';
import AssignmentTurnedInRounded from '@mui/icons-material/AssignmentTurnedInRounded';
import PaidRounded from '@mui/icons-material/PaidRounded';
import SavingsRounded from '@mui/icons-material/SavingsRounded';
import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded';
import SellRounded from '@mui/icons-material/SellRounded';
import PhoneMissedRounded from '@mui/icons-material/PhoneMissedRounded';
import AssignmentLateRounded from '@mui/icons-material/AssignmentLateRounded';
import MoneyOffRounded from '@mui/icons-material/MoneyOffRounded';
import { useFeature } from '../../ui/FeaturesProvider';
import { fetchPulseSummary, type FetchOpts } from '../../services/contactOptimizationService';
import type { PulseSummary, PulseWeeklyTrendPoint } from '../../types/contactOptimization';
import { formatDateRange } from '../../utils/date';
import SettingsPageHeader from '../settings/components/SettingsPageHeader';
import ContactOptimizationNav from './ContactOptimizationNav';
import { RefreshProvider, useRefreshRegistration, useReportFreshness } from './RefreshController';
import ChartSectionHeader from './ChartSectionHeader';
import ClockBadge from './ClockBadge';
import { SectionScopeChip } from './ScopeBanner';
import ExplainerNote, { ExplainerToggle } from './ExplainerNote';
import EChartsRenderer from '../../components/EChartsRenderer';
import ContactSpeedCohort from './contactSpeedCohort';
import { MetricTile } from './MetricTile';
import { getChartPalette } from '../../theme/charts';
import { ModeContext } from '../../theme/ThemeProvider';

function pct(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return '\u2013';
  return `${(value * 100).toFixed(digits)}%`;
}

function usd(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return '\u2013';
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

function signedPp(value: number | null | undefined): string {
  if (value === null || value === undefined) return '\u2013';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}pp`;
}

// Percent change vs. a per-week average baseline, used by the plain
// activity counts (calls/connects/agent-hours) -- these are raw volumes
// on very different scales, so a raw difference reads worse than a % move.
// Deliberately just the number, no "vs. last 4 weeks" suffix -- the
// section label above every card grid already says that once, so
// repeating it in every single pill only made some cards wider/taller
// than others and pushed the whole row into wrapping or overflowing.
function pctChangeVsWeeklyAvg(current: number | null | undefined, weeklyAvg: number | null | undefined): string | undefined {
  if (current == null || weeklyAvg == null || weeklyAvg === 0) return undefined;
  const change = (current - weeklyAvg) / weeklyAvg;
  const sign = change >= 0 ? '+' : '';
  return `${sign}${(change * 100).toFixed(0)}%`;
}

interface DeltaMeta {
  text: string | undefined;
  good: boolean | null;
  direction: 'up' | 'down' | null;
}

// Shared week-over-week % change used by the unit-economics tiles, split
// by whether "up" is good (Value/Cost Ratio) or bad (a cost-per-X metric)
// -- unlike the plain activity counts above, these get colored the same
// way the ratio-based tiles are, instead of sitting there in plain grey.
// Just the number, same reasoning as pctChangeVsWeeklyAvg above.
function pctChangeDelta(
  current: number | null | undefined,
  baseline: number | null | undefined,
  { higherIsBetter }: { higherIsBetter: boolean },
): DeltaMeta {
  if (current == null || baseline == null || baseline === 0) {
    return { text: undefined, good: null, direction: null };
  }
  const change = (current - baseline) / baseline;
  const sign = change >= 0 ? '+' : '';
  const isUp = change >= 0;
  return {
    text: `${sign}${(change * 100).toFixed(0)}%`,
    good: isUp === higherIsBetter,
    direction: isUp ? 'up' : 'down',
  };
}

// Percentage-point delta between two rates (both already 0-1 fractions),
// used by the fresh-leads tiles -- a plain point difference reads better
// than a "% change" for a rate that's already small (e.g. 6.68% vs
// 6.45% is a tiny % change but a perfectly readable +0.23pp).
function ppDelta(
  current: number | null | undefined,
  baseline: number | null | undefined,
  { higherIsBetter }: { higherIsBetter: boolean },
): DeltaMeta {
  if (current == null || baseline == null) {
    return { text: undefined, good: null, direction: null };
  }
  const diffPp = (current - baseline) * 100;
  const isUp = diffPp >= 0;
  return { text: signedPp(diffPp), good: isUp === higherIsBetter, direction: isUp ? 'up' : 'down' };
}

/**
 * "Fresh vs. everything else" as a grouped bar chart -- this week
 * against the prior-4-week baseline, side by side, so the "does calling
 * fast actually matter" question has a shape you can see instead of two
 * bare numbers to mentally subtract.
 */
function FreshVsRestChart({
  freshCurrent,
  freshBaseline,
  restCurrent,
  restBaseline,
  chartPalette,
}: {
  freshCurrent: number | null;
  freshBaseline: number | null;
  restCurrent: number | null;
  restBaseline: number | null;
  chartPalette: string[];
}) {
  const toPct = (v: number | null) => (v != null ? +(v * 100).toFixed(3) : null);
  // Series 1 = current window (primary), Series 2 = baseline comparison —
  // same order the tenant chart palette is designed for.
  const currentColor = chartPalette[0];
  const baselineColor = chartPalette[1] ?? chartPalette[0];
  const option = React.useMemo(
    () => ({
      grid: { left: 48, right: 16, top: 32, bottom: 24 },
      legend: { top: 0, textStyle: { fontSize: 11 } },
      xAxis: { type: 'category', data: ['Same day call connect', 'Anything after 0th day'], axisLabel: { fontSize: 12 } },
      yAxis: { type: 'value', axisLabel: { formatter: (v: number) => `${v.toFixed(1)}%`, fontSize: 11 } },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any[]) =>
          `<strong>${params[0]?.axisValueLabel ?? ''}</strong><br/>${params
            .filter((p) => p.value != null)
            .map((p) => `${p.marker}${p.seriesName}: ${p.value.toFixed(2)}%`)
            .join('<br/>')}`,
      },
      series: [
        {
          name: 'Prior 4 weeks',
          type: 'bar',
          data: [toPct(freshBaseline), toPct(restBaseline)],
          itemStyle: { color: baselineColor },
          barMaxWidth: 40,
        },
        {
          name: 'Last 7 days',
          type: 'bar',
          data: [toPct(freshCurrent), toPct(restCurrent)],
          itemStyle: { color: currentColor },
          barMaxWidth: 40,
          label: { show: true, position: 'top', fontSize: 11, formatter: (p: any) => (p.value != null ? `${p.value.toFixed(1)}%` : '') },
        },
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [freshCurrent, freshBaseline, restCurrent, restBaseline, currentColor, baselineColor],
  );
  return <EChartsRenderer option={option} height={220} />;
}

function formatWeekLabel(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * "Are we trending up or down" -- everything else on this page is a single
 * before/after snapshot (this week vs. one 4-week blob), which can only
 * ever answer "did this week beat last month." This chart is the one place
 * on Pulse that shows real motion over time: call volume as bars (how much
 * we dialed each week) and connect rate as a line (how well it worked),
 * on their own axes since one's a count in the thousands and the other's
 * a percentage under 5%.
 */
function WeeklyTrendChart({
  weeklyTrend,
  theme,
  chartPalette,
}: {
  weeklyTrend: PulseWeeklyTrendPoint[];
  theme: Theme;
  chartPalette: string[];
}) {
  const callsColor = chartPalette[0];
  const rateColor = chartPalette[1] ?? chartPalette[0];
  const option = React.useMemo(
    () => ({
      grid: { left: 56, right: 56, top: 32, bottom: 36 },
      legend: { top: 0, textStyle: { fontSize: 11 } },
      xAxis: {
        type: 'category',
        data: weeklyTrend.map((w) => formatWeekLabel(w.week_start)),
        axisLabel: { fontSize: 11 },
      },
      yAxis: [
        {
          type: 'value',
          name: 'Calls',
          nameTextStyle: { fontSize: 10, color: theme.palette.text.secondary },
          axisLabel: { fontSize: 10, formatter: (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`) },
          splitLine: { show: false },
        },
        {
          type: 'value',
          name: 'Connect rate',
          nameTextStyle: { fontSize: 10, color: theme.palette.text.secondary },
          axisLabel: { fontSize: 10, formatter: (v: number) => `${v.toFixed(1)}%` },
          splitLine: { show: true, lineStyle: { color: alpha(theme.palette.text.secondary, 0.12) } },
        },
      ],
      tooltip: {
        trigger: 'axis',
        formatter: (params: any[]) => {
          const idx = params[0]?.dataIndex ?? 0;
          const w = weeklyTrend[idx];
          if (!w) return '';
          const marker = (seriesName: string) => params.find((p) => p.seriesName === seriesName)?.marker ?? '';
          return `Week of ${w.week_start}<br/>${marker('Calls')}${w.total_calls.toLocaleString()} calls, ${w.total_connects.toLocaleString()} connects<br/>${marker('Connect rate')}Connect rate: ${pct(w.connect_rate, 2)}`;
        },
      },
      series: [
        {
          name: 'Calls',
          type: 'bar',
          yAxisIndex: 0,
          data: weeklyTrend.map((w) => w.total_calls),
          itemStyle: { color: callsColor },
          barMaxWidth: 28,
        },
        {
          name: 'Connect rate',
          type: 'line',
          yAxisIndex: 1,
          data: weeklyTrend.map((w) => (w.connect_rate != null ? +(w.connect_rate * 100).toFixed(3) : null)),
          smooth: false,
          symbolSize: 6,
          lineStyle: { width: 2.5, color: rateColor },
          itemStyle: { color: rateColor },
        },
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weeklyTrend, theme, callsColor, rateColor],
  );
  if (!weeklyTrend.length) return null;
  return <EChartsRenderer option={option} height={260} />;
}

export default function PulsePage() {
  return (
    <RefreshProvider>
      <PulsePageContent />
    </RefreshProvider>
  );
}

function PulsePageContent() {
  const theme = useTheme();
  const { branding } = React.useContext(ModeContext);
  const chartPalette = React.useMemo(() => getChartPalette(branding), [branding]);
  const feature = useFeature('contact_optimization');
  const [summary, setSummary] = React.useState<PulseSummary | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showTrendHelp, setShowTrendHelp] = React.useState(false);
  const [showFreshHelp, setShowFreshHelp] = React.useState(false);
  const [showGapCostHelp, setShowGapCostHelp] = React.useState(false);
  const [showOtherPopHelp, setShowOtherPopHelp] = React.useState(false);

  const load = React.useCallback((opts?: FetchOpts) => {
    setLoading(true);
    setError(null);
    return fetchPulseSummary(opts)
      .then((data) => {
        setSummary(data);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to load Pulse');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  React.useEffect(() => {
    if (!feature.enabled) return;
    load();
  }, [feature.enabled, load]);

  useRefreshRegistration(React.useCallback(() => load({ refresh: true }), [load]));
  useReportFreshness(summary?.computed_at);

  if (!feature.enabled) {
    return (
      <Box sx={{ maxWidth: 720 }}>
        <ContactOptimizationNav active="pulse" />
        <Alert severity="info">
          Contact Optimization is not turned on for this workspace. Enable it under Settings &rarr; Features.
        </Alert>
      </Box>
    );
  }

  if (loading && !summary) {
    return (
      <Box>
        <ContactOptimizationNav active="pulse" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <ContactOptimizationNav active="pulse" />
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!summary) return null;

  const {
    header,
    fresh_leads: freshLeads,
    weekly_trend: weeklyTrend,
    trend_weeks: trendWeeks,
    cohort,
    other_populations: otherPopulations,
    gap_cost: gapCost,
    period,
  } = summary;

  const costPerDialDelta = pctChangeDelta(header.unit_economics.cost_per_dial_current, header.unit_economics.cost_per_dial_baseline, { higherIsBetter: false });
  const costPerConnectDelta = pctChangeDelta(header.unit_economics.cost_per_connect_current, header.unit_economics.cost_per_connect_baseline, { higherIsBetter: false });
  const costPerApplicationDelta = pctChangeDelta(header.unit_economics.cost_per_application_current, header.unit_economics.cost_per_application_baseline, { higherIsBetter: false });
  const valueCostRatioDelta = pctChangeDelta(header.unit_economics.value_cost_ratio_current, header.unit_economics.value_cost_ratio_baseline, { higherIsBetter: true });

  const freshRateDelta = ppDelta(freshLeads.connect_rate_current, freshLeads.connect_rate_baseline, { higherIsBetter: true });
  const restRateDelta = ppDelta(freshLeads.rest_connect_rate_current, freshLeads.rest_connect_rate_baseline, { higherIsBetter: true });
  const speedPremiumCurrent =
    freshLeads.connect_rate_current != null && freshLeads.rest_connect_rate_current != null
      ? freshLeads.connect_rate_current - freshLeads.rest_connect_rate_current
      : null;
  const speedPremiumBaseline =
    freshLeads.connect_rate_baseline != null && freshLeads.rest_connect_rate_baseline != null
      ? freshLeads.connect_rate_baseline - freshLeads.rest_connect_rate_baseline
      : null;
  const speedPremiumDelta = ppDelta(speedPremiumCurrent, speedPremiumBaseline, { higherIsBetter: true });
  const freshCallsDelta = pctChangeVsWeeklyAvg(freshLeads.total_calls_current, (freshLeads.total_calls_baseline ?? 0) / 4);
  const restCallsDelta = pctChangeVsWeeklyAvg(
    freshLeads.rest_total_calls_current,
    (freshLeads.rest_total_calls_baseline ?? 0) / 4,
  );

  // Sparklines only for the 3 metrics with a real multi-week series
  // (weeklyTrend, 12 weeks) -- every other card's "delta" is just two
  // points (this week vs. a 4-week average), which isn't a trend.
  const callsSparkline = weeklyTrend.map((w) => w.total_calls);
  const connectsSparkline = weeklyTrend.map((w) => w.total_connects);
  const connectRateSparkline = weeklyTrend.map((w) => w.connect_rate ?? 0);
  const callsBaselineLabel =
    header.total_calls_baseline_weekly_avg != null ? Math.round(header.total_calls_baseline_weekly_avg).toLocaleString() : undefined;
  const connectsBaselineLabel =
    header.total_connects_baseline_weekly_avg != null
      ? Math.round(header.total_connects_baseline_weekly_avg).toLocaleString()
      : undefined;
  const connectRateBaselineLabel = header.connect_rate_baseline != null ? pct(header.connect_rate_baseline) : undefined;

  // Gap-cost tiles ("what did calling at the wrong time cost us") vs. the
  // same last-4-weeks-averaged-to-a-week baseline every other tile on this
  // page uses -- lower is better for all three (fewer missed connects/
  // sales/dollars is the goal), unlike the activity counts above.
  const missedConnectsDelta = pctChangeDelta(
    gapCost.total_missed_connects,
    gapCost.total_missed_connects_baseline_weekly_avg,
    { higherIsBetter: false },
  );
  const missedConnectsBaselineLabel =
    gapCost.total_missed_connects_baseline_weekly_avg != null
      ? gapCost.total_missed_connects_baseline_weekly_avg.toFixed(1)
      : undefined;
  const casLostDelta = pctChangeDelta(
    gapCost.estimated_cas_lost,
    gapCost.estimated_cas_lost_baseline_weekly_avg,
    { higherIsBetter: false },
  );
  const casLostBaselineLabel =
    gapCost.estimated_cas_lost_baseline_weekly_avg != null ? gapCost.estimated_cas_lost_baseline_weekly_avg.toFixed(1) : undefined;
  const commissionLostDelta = pctChangeDelta(
    gapCost.estimated_commission_lost,
    gapCost.estimated_commission_lost_baseline_weekly_avg,
    { higherIsBetter: false },
  );
  const commissionLostBaselineLabel =
    gapCost.estimated_commission_lost_baseline_weekly_avg != null
      ? usd(gapCost.estimated_commission_lost_baseline_weekly_avg, 0)
      : undefined;

  // The page default -- dropped directly into every section that actually
  // uses it, not just stated once at the top, so no section ever has to be
  // taken on faith from a banner several scrolls above it.
  const defaultWindow = {
    label: 'Last 7 days vs. prior 4 weeks',
    detail: `${formatDateRange(period.current_start, period.current_end)} vs. ${formatDateRange(period.baseline_start, period.baseline_end)} \u2014 a rolling window that shifts by one day, every day, not a calendar Monday\u2013Sunday week`,
  };

  return (
    <Box>
      <ContactOptimizationNav active="pulse" />
      <SettingsPageHeader description="Pulse answers 'how are we doing right now' — the last 7 days of calling activity, compared against the four weeks before that." />

      <Paper
        elevation={0}
        sx={(t) => ({
          p: 2,
          mb: 2.5,
          border: `1px solid ${t.tokens.border.subtle}`,
          borderRadius: `${t.ds.radius.md}px`,
          bgcolor: t.tokens.surface.raised,
        })}
      >
        <ChartSectionHeader
          title="Dialing Metrics"
          subtitle="Company-wide calling activity, plus what calling at the wrong time (or not at all) is estimated to be costing us. Rolling 7-day window, compared against the average of the 4 weeks before it (not a fixed Monday–Sunday week)."
          actions={
            <>
              <SectionScopeChip window={defaultWindow} />
              <ClockBadge basis="lead_local" />
              <ExplainerToggle title="How the cost-of-mistiming tiles are calculated" open={showGapCostHelp} onClick={() => setShowGapCostHelp((v) => !v)} />
            </>
          }
        />
        <ExplainerNote title="How the cost-of-mistiming tiles are calculated" open={showGapCostHelp}>
          {`For every qualifying fresh lead created in the current window (a rolling window that moves forward one day at a time, not a fixed Monday\u2013Sunday week; same population Queue uses: a real lead, a known state, never marked do-not-call, no sale yet):

- **Called at the wrong hour** — we look up the connect rate for the specific hour and state this lead was actually called in (the same numbers shown on the Timing tab), and compare it against that same state's single best hour. The difference between the two is counted as an estimated "missed connect" — a call that likely would have been answered, had it happened at the better hour.
- **Never called at all** — scored as a full miss: we assume it would have connected at that state's best-hour rate, since it never got a chance to connect at any hour.

Missed connects convert into **missed sales** using our own measured rate of how often a connected call turns into a submitted application (we call a submitted application a "CAS"): ${pct(gapCost.connect_to_cas_pct != null ? gapCost.connect_to_cas_pct / 100 : null, 0)} of connected first calls go on to submit one. Missed sales then convert into **missed dollars** using real premium and commission-per-application figures from carrier data — never a flat, made-up rate.

The "vs. the last 4 weeks" comparison on these three tiles works the same way as every other tile below: we run this exact same calculation over the 4 weeks before the current window, then divide that total by 4 to get a weekly average we can fairly compare against the current ~7-day total.

This is a different, more conservative number than an earlier version of this page used. That earlier version compared against each state's single best hour based only on that hour's own raw data, which can be noisy when a state doesn't have many calls in that hour. This version instead compares against the same "blended" best-hour rate the Timing tab shows — one that leans more on a state's own data when there's plenty of it, and leans more on the floor-wide average when there isn't — so the two pages always agree on what's realistically achievable for a state.

**Gross margin missed** stays blank — that needs real per-agent cost data, which we don't have yet.`}
        </ExplainerNote>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, mb: 1 }}>
          <PhoneMissedRounded sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            What did calling at the wrong time — or not calling at all — cost us?
          </Typography>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            // Same 220px minmax as every other metric-tile grid on this page
            // (see the comment on "Overall calling activity" below) --
            // one consistent tile width across the whole page, not a
            // section-by-section choice.
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 1.5,
          }}
        >
          <MetricTile
            icon={PhoneMissedRounded}
            label="Missed connects"
            value={gapCost.total_missed_connects.toFixed(1)}
            delta={missedConnectsDelta.text}
            deltaGood={missedConnectsDelta.good}
            deltaDirection={missedConnectsDelta.direction}
            deltaBaselineLabel={missedConnectsBaselineLabel}
            tooltip={`${gapCost.mistimed_missed_connects.toFixed(1)} from calling at a weaker hour than this state's best, plus ${gapCost.never_called_missed_connects.toFixed(1)} from ${gapCost.never_called_count.toLocaleString()} leads never called at all — out of ${gapCost.mql_population.toLocaleString()} qualifying fresh leads in the last 7 days. The change (in parentheses) is against the last 4 weeks' average per-week total.`}
          />
          <MetricTile
            icon={AssignmentLateRounded}
            label="Sales missed (CAS)"
            value={gapCost.estimated_cas_lost != null ? gapCost.estimated_cas_lost.toFixed(1) : '\u2013'}
            delta={casLostDelta.text}
            deltaGood={casLostDelta.good}
            deltaDirection={casLostDelta.direction}
            deltaBaselineLabel={casLostBaselineLabel}
            tooltip={`"CAS" is our shorthand for a submitted insurance application. This is our estimate of how many of the missed connects above would have gone on to submit one, using our measured rate: ${pct(gapCost.connect_to_cas_pct != null ? gapCost.connect_to_cas_pct / 100 : null, 0)} of connected first calls go on to submit an application. The change (in parentheses) is against the last 4 weeks' average per-week total.`}
          />
          <MetricTile
            icon={MoneyOffRounded}
            label="Dollars missed"
            value={gapCost.estimated_commission_lost != null ? usd(gapCost.estimated_commission_lost, 0) : '\u2013'}
            delta={commissionLostDelta.text}
            deltaGood={commissionLostDelta.good}
            deltaDirection={commissionLostDelta.direction}
            deltaBaselineLabel={commissionLostBaselineLabel}
            tooltip={`The commission we estimate we missed out on from those missed sales: ${usd(gapCost.estimated_premium_lost, 0)} of premium, multiplied by our average commission rate per application. This figure is ${gapCost.dollar_basis.is_measured ? 'measured' : 'estimated'} from real carrier data. It's a gross figure — before subtracting lead cost, overhead, or chargebacks. The change (in parentheses) is against the last 4 weeks' average per-week total.`}
          />
          <MetricTile
            icon={SavingsRounded}
            label="Gross margin missed"
            value=""
            unavailableNote={gapCost.gross_margin_note}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <PhoneRounded sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Overall calling activity
          </Typography>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            // 220px, not 190px -- the longest titles ("Connects / agent-hour")
            // need roughly that much to sit on one line without the ellipsis
            // truncation the label's noWrap would otherwise apply.
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 1.5,
          }}
        >
        <MetricTile
          icon={PhoneRounded}
          label="Calls dialed"
          value={header.total_calls_current.toLocaleString()}
          delta={pctChangeVsWeeklyAvg(header.total_calls_current, header.total_calls_baseline_weekly_avg)}
          deltaBaselineLabel={callsBaselineLabel}
          sparklineValues={callsSparkline}
          sparklineColor={chartPalette[0]}
          tooltip="Every outbound call attempt in the last 7 days, to every lead, regardless of whether it connected."
        />
        <MetricTile
          icon={PhoneInTalkRounded}
          label="Real connects"
          value={header.total_connects_current.toLocaleString()}
          delta={pctChangeVsWeeklyAvg(header.total_connects_current, header.total_connects_baseline_weekly_avg)}
          deltaBaselineLabel={connectsBaselineLabel}
          sparklineValues={connectsSparkline}
          sparklineColor={chartPalette[0]}
          tooltip="Calls in the last 7 days that connected and stayed on the line for at least 90 seconds — our definition of a real connect, not just a phone picked up."
        />
        <MetricTile
          icon={TrendingUpRounded}
          label="Connect rate"
          value={pct(header.connect_rate_current)}
          delta={signedPp(header.connect_rate_delta_pp)}
          deltaGood={header.connect_rate_delta_pp != null ? header.connect_rate_delta_pp >= 0 : null}
          deltaDirection={header.connect_rate_delta_pp != null ? (header.connect_rate_delta_pp >= 0 ? 'up' : 'down') : null}
          deltaBaselineLabel={connectRateBaselineLabel}
          sparklineValues={connectRateSparkline}
          tooltip={'Share of calls that turned into a real connect (90+ seconds), over the last 7 days vs. the last 4 weeks. The change is shown in percentage points (pp) \u2014 e.g. going from 10% to 12% is "+2pp," not "+20%."'}
        />
        <MetricTile
          icon={SpeedRounded}
          label="Connects / agent-hour"
          value={header.connects_per_agent_hour_current != null ? header.connects_per_agent_hour_current.toFixed(2) : '\u2013'}
          delta={
            header.connects_per_agent_hour_delta != null
              ? `${header.connects_per_agent_hour_delta >= 0 ? '+' : ''}${header.connects_per_agent_hour_delta.toFixed(2)}`
              : undefined
          }
          deltaGood={header.connects_per_agent_hour_delta != null ? header.connects_per_agent_hour_delta >= 0 : null}
          deltaDirection={header.connects_per_agent_hour_delta != null ? (header.connects_per_agent_hour_delta >= 0 ? 'up' : 'down') : null}
          deltaBaselineLabel={
            header.connects_per_agent_hour_baseline != null ? header.connects_per_agent_hour_baseline.toFixed(2) : undefined
          }
          tooltip="How many real connects we got per hour an agent was actively dialing. A rough estimate, by design — the agent-hours denominator here is measured from actual dials, not from a shift schedule (which, even where real, is only a current assignment snapshot, not a verified log of who worked a specific past hour), so this can undercount how much time was actually worked."
        />
        <MetricTile
          icon={AssignmentTurnedInRounded}
          label="Applications"
          value={header.app_complete_current.toLocaleString()}
          delta={
            header.app_complete_rate_delta_pp != null
              ? `${signedPp(header.app_complete_rate_delta_pp)} rate`
              : undefined
          }
          deltaGood={header.app_complete_rate_delta_pp != null ? header.app_complete_rate_delta_pp >= 0 : null}
          deltaDirection={header.app_complete_rate_delta_pp != null ? (header.app_complete_rate_delta_pp >= 0 ? 'up' : 'down') : null}
          deltaBaselineLabel={header.app_complete_baseline.toLocaleString()}
          tooltip={`Calls in the last 7 days where the agent got as far as a quote or a submitted application (${pct(header.app_complete_rate_current, 2)} of all calls), vs. the last 4 weeks. A step that happens earlier and more often than a completed sale, so it's a faster early read on whether calls are going well.`}
        />
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={(t) => ({
          p: 2,
          mb: 2.5,
          border: `1px solid ${t.tokens.border.subtle}`,
          borderRadius: `${t.ds.radius.md}px`,
          bgcolor: t.tokens.surface.raised,
        })}
      >
        <ChartSectionHeader
          title="Unit economics"
          subtitle="Lead acquisition cost and the premium it's expected to bring back. A deal can close up to 90 days after this window ends and still count toward these numbers, so these figures can keep shifting for a while afterward."
          actions={<SectionScopeChip window={defaultWindow} />}
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 1.5,
            mt: 1.5,
          }}
        >
        <MetricTile
          icon={PaidRounded}
          label="Cost / dial"
          value={usd(header.unit_economics.cost_per_dial_current)}
          delta={costPerDialDelta.text}
          deltaGood={costPerDialDelta.good}
          deltaDirection={costPerDialDelta.direction}
          deltaBaselineLabel={header.unit_economics.cost_per_dial_baseline != null ? usd(header.unit_economics.cost_per_dial_baseline) : undefined}
          tooltip={`What we spent acquiring leads in the last 7 days (ad spend, affiliate fees, etc. — counted once per distinct lead dialed, not once per call), divided by every call made regardless of outcome. ${pct(header.unit_economics.spend_coverage_pct_current, 0)} of leads dialed have a known cost; the rest are from free channels (referrals, organic search) and correctly show $0.`}
        />
        <MetricTile
          icon={PaidRounded}
          label="Cost / connect"
          value={usd(header.unit_economics.cost_per_connect_current)}
          delta={costPerConnectDelta.text}
          deltaGood={costPerConnectDelta.good}
          deltaDirection={costPerConnectDelta.direction}
          deltaBaselineLabel={header.unit_economics.cost_per_connect_baseline != null ? usd(header.unit_economics.cost_per_connect_baseline) : undefined}
          tooltip="Same acquisition spend as Cost / dial, but divided by real connects (90+ second calls) instead of every call. Always higher than Cost / dial, since not every call connects."
        />
        <MetricTile
          icon={PaidRounded}
          label="Cost / application"
          value={usd(header.unit_economics.cost_per_application_current)}
          delta={costPerApplicationDelta.text}
          deltaGood={costPerApplicationDelta.good}
          deltaDirection={costPerApplicationDelta.direction}
          deltaBaselineLabel={
            header.unit_economics.cost_per_application_baseline != null ? usd(header.unit_economics.cost_per_application_baseline) : undefined
          }
          tooltip="Same acquisition spend again, divided by completed applications (a quote or submitted application) instead of calls or connects. The highest of the three cost figures, since it takes many calls to produce one application."
        />
        <MetricTile
          icon={SavingsRounded}
          label="Value / cost ratio"
          value={header.unit_economics.value_cost_ratio_current != null ? `${header.unit_economics.value_cost_ratio_current.toFixed(2)}x` : '\u2013'}
          delta={valueCostRatioDelta.text}
          deltaGood={valueCostRatioDelta.good}
          deltaDirection={valueCostRatioDelta.direction}
          deltaBaselineLabel={
            header.unit_economics.value_cost_ratio_baseline != null ? `${header.unit_economics.value_cost_ratio_baseline.toFixed(2)}x` : undefined
          }
          tooltip={`Expected premium dollars back for every $1 spent acquiring these leads: the premium on applications they submitted, times the historical share of that premium that actually ends up paid (currently ${pct(header.unit_economics.submit_to_paid_rate, 1)}, based on dollars not just a headcount of applications), divided by acquisition spend. Above 1.0x means we expect to make back more than we spent.`}
        />
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={(t) => ({
          p: 2,
          mb: 2.5,
          border: `1px solid ${t.tokens.border.subtle}`,
          borderRadius: `${t.ds.radius.md}px`,
          bgcolor: t.tokens.surface.raised,
        })}
      >
        <ChartSectionHeader
          title="Does calling a lead fast actually matter?"
          subtitle="Two separate connect rates, shown side by side — they measure different groups of calls, so they won't add up to 100%."
          actions={
            <>
              <SectionScopeChip window={defaultWindow} />
              <ExplainerToggle title="Why 'fresh' gets its own comparison" open={showFreshHelp} onClick={() => setShowFreshHelp((v) => !v)} />
            </>
          }
        />
        <ExplainerNote title="Why 'fresh' gets its own comparison" open={showFreshHelp}>
          {`- **Same day call connect** = the very first call to a lead, made the same day it came in (day 0).
- **Anything after 0th day** = every other call — later attempts, or a first call made a day or more after the lead came in.

**Speed premium** = same-day rate minus anything-after-0th-day rate, over the last 7 days — roughly what calling same-day is worth on its own. The two volume tiles show how many calls fell into each bucket; if the day-0 count is small, the premium can't move much company-wide.`}
        </ExplainerNote>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '2fr 1.4fr' },
            gap: 2,
            mt: 1.5,
            alignItems: 'stretch',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 1.5,
              alignContent: 'start',
            }}
          >
            <MetricTile
              icon={BoltRounded}
              label="Same day call connect"
              value={pct(freshLeads.connect_rate_current)}
              delta={freshRateDelta.text}
              deltaGood={freshRateDelta.good}
              deltaDirection={freshRateDelta.direction}
              deltaBaselineLabel={freshLeads.connect_rate_baseline != null ? pct(freshLeads.connect_rate_baseline) : undefined}
              tooltip="Connect rate for calls that were the very first call to a lead, made the same day the lead came in (day 0). Its own rate, out of every same-day first call — not a share of all calls."
            />
            <MetricTile
              icon={AccessTimeRounded}
              label="Anything after 0th day"
              value={pct(freshLeads.rest_connect_rate_current)}
              delta={restRateDelta.text}
              deltaGood={restRateDelta.good}
              deltaDirection={restRateDelta.direction}
              deltaBaselineLabel={freshLeads.rest_connect_rate_baseline != null ? pct(freshLeads.rest_connect_rate_baseline) : undefined}
              tooltip="Connect rate for every other call: later attempts, or a first call made a day or more after the lead came in. Its own rate, out of every one of those calls — a completely separate number from Same day call connect above."
            />
            <MetricTile
              icon={TrendingUpRounded}
              label="Speed premium"
              value={speedPremiumCurrent != null ? signedPp(speedPremiumCurrent * 100) : '\u2013'}
              delta={speedPremiumDelta.text}
              deltaGood={speedPremiumDelta.good}
              deltaDirection={speedPremiumDelta.direction}
              deltaBaselineLabel={speedPremiumBaseline != null ? signedPp(speedPremiumBaseline * 100) : undefined}
              tooltip="Same-day rate minus anything-after-0th-day rate, over the last 7 days — the one number that directly compares the two tiles to the left. How much of a connect-rate edge calling same-day is worth right now."
            />
            <MetricTile
              icon={PhoneRounded}
              label="Number of calls made on 0th day in last 7 days"
              value={freshLeads.total_calls_current.toLocaleString()}
              delta={freshCallsDelta}
              deltaBaselineLabel={Math.round((freshLeads.total_calls_baseline ?? 0) / 4).toLocaleString()}
              tooltip="How many calls in the last 7 days were a lead's very first call, made the same day it came in (day 0). If this is small next to total calls dialed, the speed premium above has limited room to move company-wide results — the limiter is volume, not the size of the effect."
            />
            <MetricTile
              icon={PhoneRounded}
              label="Number of calls made after 0th day in last 7 days"
              value={(freshLeads.rest_total_calls_current ?? 0).toLocaleString()}
              delta={restCallsDelta}
              deltaBaselineLabel={Math.round((freshLeads.rest_total_calls_baseline ?? 0) / 4).toLocaleString()}
              tooltip="How many calls in the last 7 days were not a same-day first attempt — later attempts, or a first call made a day or more after the lead came in. Pair this with the day-0 volume tile to see how the call mix breaks down."
            />
          </Box>
          <FreshVsRestChart
            freshCurrent={freshLeads.connect_rate_current}
            freshBaseline={freshLeads.connect_rate_baseline}
            restCurrent={freshLeads.rest_connect_rate_current}
            restBaseline={freshLeads.rest_connect_rate_baseline}
            chartPalette={chartPalette}
          />
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={(t) => ({
          p: 2,
          mb: 2.5,
          border: `1px solid ${t.tokens.border.subtle}`,
          borderRadius: `${t.ds.radius.md}px`,
          bgcolor: t.tokens.surface.raised,
        })}
      >
        <ChartSectionHeader
          title="Connects by call type"
          subtitle="Booked appointments and resold leads aren't counted in any number above — comparing them to a fresh, first-time call wouldn't be fair to either. Same three metrics for each type."
          actions={
            <>
              <SectionScopeChip window={{ label: 'Last 7 days only', detail: `${formatDateRange(period.current_start, period.current_end)} — no 4-week baseline here` }} />
              <ExplainerToggle
                title="Why these are kept separate"
                open={showOtherPopHelp}
                onClick={() => setShowOtherPopHelp((v) => !v)}
              />
            </>
          }
        />
        <ExplainerNote title="Why these are kept separate" open={showOtherPopHelp}>
          {`Both columns show the same three underlying metrics: **Calls**, a 90+ second connect rate ÷ calls, and **Sales (CAS)** (distinct leads in that group who went on to submit an application) — just labeled differently for what the connect rate actually means in each population.

**Scheduled appointments** — a booked callback is a kept-commitment problem, not a cold-call timing one. Once a lead has an appointment on the books, the question is "did we reach them when we called," so that rate is labeled **Kept rate** here rather than Connect rate.

**Shared sold / resale pool** — resold leads are worked by many buyers long after their original lead date, on an ongoing basis. Their connect rate is structurally lower than a brand-new lead's, for reasons that have nothing to do with calling speed or timing, so comparing it to the fresh-lead numbers above would be misleading.

Both use the same 90-second connect definition as the rest of this page, just measured against a different population, over the same last-7-days window.`}
        </ExplainerNote>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            mt: 1.5,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <EventAvailableRounded sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Scheduled appointments
              </Typography>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 1.5,
              }}
            >
              <MetricTile
                icon={PhoneRounded}
                label="Calls"
                value={otherPopulations.appointment_calls.toLocaleString()}
                tooltip="Calls made to a lead after they had a scheduled-appointment status on file, on or before that call — over the last 7 days."
              />
              <MetricTile
                icon={PhoneInTalkRounded}
                label="Kept rate"
                value={pct(otherPopulations.appointments_kept_rate, 1)}
                tooltip="Share of appointment calls that connected for 90+ seconds — did we actually reach the lead when we called for the appointment, out of every appointment call made."
              />
              <MetricTile
                icon={PaidRounded}
                label="Sales (CAS)"
                value={otherPopulations.cas_from_appointments.toLocaleString()}
                tooltip="Number of distinct leads who had an appointment call in the last 7 days and went on to submit an application (CAS). Counted once per lead, even if they had more than one appointment call."
              />
            </Box>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
          <Divider sx={{ display: { xs: 'block', md: 'none' } }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <SellRounded sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Shared sold / resale pool
              </Typography>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 1.5,
              }}
            >
              <MetricTile
                icon={PhoneRounded}
                label="Calls"
                value={otherPopulations.sold_pool_dials.toLocaleString()}
                tooltip="Calls in the last 7 days to leads already flagged as resold to another buyer before that call happened."
              />
              <MetricTile
                icon={PhoneInTalkRounded}
                label="Connect rate"
                value={pct(otherPopulations.sold_pool_connect_rate, 1)}
                tooltip="Sold-pool connects divided by sold-pool calls, over the last 7 days. Expect this lower than the fresh-lead connect rate above — it's not a comparable population, by design."
              />
              <MetricTile
                icon={PaidRounded}
                label="Sales (CAS)"
                value={(otherPopulations.cas_from_sold_pool ?? 0).toLocaleString()}
                tooltip="Number of distinct leads who had a sold/resale-pool call in the last 7 days and went on to submit an application (CAS). Counted once per lead, even if they had more than one sold-pool call."
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={(t) => ({
          p: 2,
          mb: 2.5,
          border: `1px solid ${t.tokens.border.subtle}`,
          borderRadius: `${t.ds.radius.md}px`,
          bgcolor: t.tokens.surface.raised,
        })}
      >
        <ChartSectionHeader
          title={`Connect rate & call volume, last ${trendWeeks} weeks`}
          subtitle={`Spans the last ${trendWeeks} weeks, not just the current-vs-baseline window every other section above uses.`}
          actions={
            <>
              <SectionScopeChip window={{ label: `Last ${trendWeeks} weeks`, detail: 'Monday\u2013Sunday buckets, not the this-week-vs-baseline window' }} />
              <ExplainerToggle title="Why this chart exists" open={showTrendHelp} onClick={() => setShowTrendHelp((v) => !v)} />
            </>
          }
        />
        <ExplainerNote title="Why this chart exists" open={showTrendHelp}>
          {`Every other number on this page is a single before/after comparison (the last 7 days vs. one 4-week blob), which can only ever answer "did the last 7 days beat last month." This chart is the one place that shows real motion over time.

- **Bars (left axis)** = total calls dialed that week, company-wide.
- **Line (right axis)** = that week's connect rate.

Each week runs Monday-to-Sunday and uses the same call data as the rest of the page, just grouped week by week instead of into one current/baseline split. The most recent bar may be a partial week if today falls in the middle of it.`}
        </ExplainerNote>
        <WeeklyTrendChart weeklyTrend={weeklyTrend} theme={theme} chartPalette={chartPalette} />
      </Paper>

      <ContactSpeedCohort rows={cohort} />
    </Box>
  );
}
